
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';
import { useNavigate } from 'react-router-dom';

type Profile = Tables<'profiles'>;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string, role: 'landlord' | 'tenant', trialPlan?: string, customRedirectUrl?: string) => Promise<{ error: any; isExistingUser?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: (redirectTo?: string) => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  const checkUserRole = async (userId: string): Promise<string | null> => {
    try {
      // Check user_roles table (primary source for role-based access)
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .is('expires_at', null)
        .or(`expires_at.gt.${new Date().toISOString()}`)
        .maybeSingle();

      if (roleData?.role) {
        return roleData.role;
      }

      // Fallback to profiles table for backward compatibility
      const { data: profileData } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', userId)
        .single();

      return profileData?.role || null;
    } catch (error) {
      console.error('Error checking user role:', error);
      return null;
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state change:', event, session?.user?.email);
        
        // Handle email confirmation - prevent auto-login
        if (event === 'SIGNED_IN' && session?.user) {
          const currentUrl = window.location.href;
          const urlParams = new URLSearchParams(window.location.search);
          
          // Check if this is an email verification callback or auto-login
          if (currentUrl.includes('/auth/callback') || 
              currentUrl.includes('type=signup') || 
              urlParams.get('type') === 'signup' ||
              currentUrl.includes('email-verified') ||
              // Detect users who just verified email (no previous sign-in)
              (session.user.email_confirmed_at && !session.user.last_sign_in_at)) {
            
            console.log('🔒 Email verification auto-login detected - signing out immediately');
            
            // Sign out the user immediately to prevent auto-login
            await supabase.auth.signOut();
            
            // Get role from URL params if available
            const role = urlParams.get('role') || 'landlord';
            
            // Redirect to email verified page with verified flag
            if (!window.location.pathname.includes('/email-verified')) {
              window.location.href = `/email-verified?role=${role}&verified=true`;
            }
            return;
          }
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer profile fetching to avoid deadlock
        if (session?.user) {
          setTimeout(() => {
            fetchProfile(session.user.id).then(setProfile);
          }, 0);
        } else {
          setProfile(null);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('🔍 Initial session check:', session?.user?.email);
      
      // Check if current URL indicates email verification
      const currentUrl = window.location.href;
      const urlParams = new URLSearchParams(window.location.search);
      
      if (session?.user && (
        currentUrl.includes('/auth/callback') || 
        currentUrl.includes('type=signup') || 
        urlParams.get('type') === 'signup'
      )) {
        console.log('🔒 Initial session is from email verification - signing out');
        supabase.auth.signOut();
        const role = urlParams.get('role') || 'landlord';
        window.location.href = `/email-verified?role=${role}`;
        return;
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setTimeout(() => {
          fetchProfile(session.user.id).then(profile => {
            if (profile) {
              setProfile(profile);
            }
          });
        }, 0);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = useCallback(async (email: string, password: string, firstName: string, lastName: string, role: 'landlord' | 'tenant', trialPlan?: string, customRedirectUrl?: string) => {
    console.log('🔄 Starting signup process for:', email, { firstName, lastName, role });
    
    // Use production domain for email redirects to prevent "page not found" errors
    const baseUrl = window.location.hostname === 'localhost' ? window.location.origin : 'https://rentflow.space';
    const redirectUrl = customRedirectUrl || `${baseUrl}/auth/callback?role=${role}`;
    console.log('🌐 Redirect URL:', redirectUrl);
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName,
            role: role
          }
        }
      });
      
      console.log('📤 Supabase signup response:', { 
        user: data.user ? { 
          id: data.user.id, 
          email: data.user.email, 
          email_confirmed_at: data.user.email_confirmed_at 
        } : null,
        session: data.session ? 'exists' : 'null',
        error: error 
      });
      
      if (error) {
        console.error('❌ Signup error:', error);
        return { error, isExistingUser: false };
      }
      
      // For tenant signups, don't try to get user immediately - let email confirmation handle it
      if (role === 'tenant') {
        console.log('✅ Tenant signup completed - email confirmation required');
        return { error: null };
      }
      
      // If user exists but is unconfirmed, data.user exists but data.session is null
      const isExistingUser = data.user && !data.session && !data.user.email_confirmed_at;
      console.log('👤 User status:', { isExistingUser, hasSession: !!data.session });
      
      // For new users, verify profile creation
      if (data.user && !isExistingUser) {
        console.log('✨ New user created, verifying profile creation...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', data.user.id)
          .maybeSingle();
        
        if (profileError) {
          console.error('❌ Error checking profile:', profileError);
        } else if (profile) {
          console.log('✅ Profile created successfully:', profile);
        } else {
          console.log('⚠️  Profile not found, creating manually...');
          
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              user_id: data.user.id,
              email: data.user.email || email,
              first_name: firstName,
              last_name: lastName,
              role
            });
          
          if (insertError) {
            console.error('❌ Failed to create profile manually:', insertError);
          } else {
            console.log('✅ Profile created manually as fallback');
          }
        }
      }
      
      console.log('🎯 Signup completed successfully');
      return { error: null, isExistingUser };
      
    } catch (err) {
      console.error('💥 Unexpected error during signup:', err);
      return { error: { message: 'Unexpected error occurred' }, isExistingUser: false };
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      console.log('🔄 Starting sign-in for:', email);
      
      // Validate session before attempting sign-in
      const { data: { session: existingSession } } = await supabase.auth.getSession();
      if (existingSession) {
        const expiresAt = existingSession.expires_at;
        const now = Math.floor(Date.now() / 1000);
        
        // Clear expired session
        if (expiresAt && expiresAt < now) {
          console.log('Clearing expired session before sign-in');
          await supabase.auth.signOut();
        }
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      // Check if user needs email confirmation
      if (error?.message?.includes('Email not confirmed')) {
        console.log('📧 Email not confirmed for:', email);
        return { error: { ...error, needsConfirmation: true } };
      }
      
      if (error) {
        console.error('❌ Sign-in error:', error);
        return { error };
      }
      
      console.log('✅ Sign-in successful for:', email);
      return { error: null };
    } catch (err) {
      console.error('💥 Unexpected error during sign-in:', err);
      return { error: { message: 'Unexpected error occurred during sign-in' } };
    }
  }, []);

  const signOut = useCallback(async (redirectTo: string = '/') => {
    await supabase.auth.signOut();
    navigate(redirectTo);
  }, [navigate]);

  const updateProfile = useCallback(async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error('No user logged in') };

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('user_id', user.id);

    if (!error && profile) {
      setProfile({ ...profile, ...updates });
    }

    return { error };
  }, [user, profile]);

  const value = useMemo(() => ({
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile
  }), [user, session, profile, loading, signUp, signIn, signOut, updateProfile]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
